import { ConfigurableColumns } from '@/components/ConfigurableColumns';

import {
  GenericDateFunctionField,
  GenericRelativeDateDisplay,
} from '@/components/GenericDateFunctionField';
import { formatBytes } from '@/components/util';
import {
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Tooltip,
} from '@mui/material';
import { useState } from 'react';
import { FunctionField } from 'react-admin';
import { DownloadFileButton } from './DataExportsCommandPannel';

const ExportErrorChip = ({ error }: { error: string }) => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Tooltip title="Export failed — click to see the error">
        <Chip
          label="Failed"
          color="error"
          onClick={() => setOpen(true)}
          style={{ cursor: 'pointer' }}
        />
      </Tooltip>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Export error</DialogTitle>
        <DialogContent>
          <pre
            style={{
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              margin: 0,
            }}
          >
            {error}
          </pre>
        </DialogContent>
      </Dialog>
    </>
  );
};

const FailedDash = () => (
  <Chip label="—" size="small" variant="outlined" />
);

export const columns: ConfigurableColumns[] = [
  {
    label: 'Created At',
    source: 'createdAt',
    sortable: true,
    GridComponent: GenericDateFunctionField,
    exportFn: record => record.createdAt?.toISOString(),
  },
  {
    label: 'Entity',
    source: 'collectionName',
    GridComponent: FunctionField,
    gridComponentProps: {
      render: (record: any) => <Chip label={record.collectionName} />,
    },
  },
  {
    label: 'Export Duration',
    source: 'exportDuration',
    GridComponent: FunctionField,
    gridComponentProps: {
      render: (record: any) =>
        record.exportDuration ? (
          <Chip
            label={`${record.exportDuration / 1000}s`}
            color="primary"
          />
        ) : record.error ? (
          <FailedDash />
        ) : (
          <Chip label="Pending" color="warning" />
        ),
    },
  },
  {
    label: 'Completed At',
    source: 'completedAt',
    GridComponent: FunctionField,
    gridComponentProps: {
      render: (record: any) =>
        record.error ? (
          <ExportErrorChip error={record.error} />
        ) : record.completedAt ? (
          <GenericRelativeDateDisplay
            date={record.completedAt}
            color="success"
          />
        ) : (
          <Chip label="Pending" color="warning" />
        ),
    },
  },
  {
    label: 'Exported Records',
    source: 'exportedRecords',
    GridComponent: FunctionField,
    gridComponentProps: {
      render: (record: any) =>
        record.exportedRecords ? (
          <Chip label={record.exportedRecords} color="primary" />
        ) : record.error ? (
          <FailedDash />
        ) : (
          <Chip label="Pending" color="warning" />
        ),
    },
  },
  {
    label: 'File Size',
    source: 'fileSize',
    GridComponent: FunctionField,
    gridComponentProps: {
      render: (record: any) =>
        record.fileSize ? (
          <Chip
            label={formatBytes(record.fileSize)}
            color="primary"
          />
        ) : record.error ? (
          <FailedDash />
        ) : (
          <Chip label="Pending" color="warning" />
        ),
    },
  },
  {
    label: 'Download File',
    source: 'filePath',
    GridComponent: FunctionField,
    gridComponentProps: {
      render: (record: any) =>
        record.fileSize ? (
          <DownloadFileButton filePath={record.filePath} />
        ) : record.error ? (
          <FailedDash />
        ) : (
          <Chip label="Pending" color="warning" />
        ),
    },
  },
];
